import type { SplatBackdrop } from "../scene/splatBackdrop";
import type { Scene as SceneJson } from "../data/types";

/**
 * Live-tune the splat backdrop's position / rotation / scale.
 *
 * The COLMAP .ply isn't naturally in calibration coords (it has its own
 * arbitrary world frame too), so the user almost always needs to translate
 * + rotate + scale it once to align with the court. This panel keeps the
 * iteration loop fast. The chosen values can be copy-pasted into the
 * exporter's --splat-position / --splat-rotation-euler / --splat-scale
 * flags so they survive a re-export.
 */
export function setupSplatTuner(
  scene: SceneJson,
  backdrop: SplatBackdrop,
): void {
  if (!scene.splat) return;
  const root = document.getElementById("splat-tuner") as HTMLDivElement;
  if (!root) return;

  root.style.display = "block";
  root.innerHTML = "";

  const initial = {
    px: scene.splat.position?.[0] ?? 0,
    py: scene.splat.position?.[1] ?? 0,
    pz: scene.splat.position?.[2] ?? 0,
    rx: scene.splat.rotation_euler?.[0] ?? 0,
    ry: scene.splat.rotation_euler?.[1] ?? 0,
    rz: scene.splat.rotation_euler?.[2] ?? 0,
    s: typeof scene.splat.scale === "number" ? scene.splat.scale : 1,
  };

  const state = { ...initial };

  // The transforms baked into scene.json are normally already correct, so
  // collapse the panel by default; clicking the header expands it.
  const header = document.createElement("div");
  header.className = "splat-tuner-header";
  header.textContent = "▸ splat backdrop";
  header.title = "Click to expand splat alignment controls";
  header.style.cursor = "pointer";
  header.style.userSelect = "none";
  root.appendChild(header);

  const body = document.createElement("div");
  body.className = "splat-tuner-body";
  body.style.display = "none";
  root.appendChild(body);

  header.addEventListener("click", () => {
    const open = body.style.display === "none";
    body.style.display = open ? "block" : "none";
    header.textContent = (open ? "▾ " : "▸ ") + "splat backdrop";
  });

  function addSlider(
    label: string, key: keyof typeof state,
    min: number, max: number, step: number,
  ): void {
    const row = document.createElement("label");
    row.className = "splat-row";
    const lab = document.createElement("span");
    lab.className = "splat-label";
    lab.textContent = label;
    const inp = document.createElement("input");
    inp.type = "range"; inp.min = String(min); inp.max = String(max);
    inp.step = String(step); inp.value = String(state[key]);
    const out = document.createElement("span");
    out.className = "splat-readout";
    out.textContent = state[key].toFixed(2);
    inp.addEventListener("input", () => {
      state[key] = Number(inp.value);
      out.textContent = state[key].toFixed(2);
      backdrop.setTransform({
        position: [state.px, state.py, state.pz],
        rotation_euler: [state.rx, state.ry, state.rz],
        scale: state.s,
      });
    });
    row.append(lab, inp, out);
    body.appendChild(row);
  }

  addSlider("x", "px", -20, 20, 0.05);
  addSlider("y", "py", -10, 10, 0.05);
  addSlider("z", "pz", -20, 20, 0.05);
  addSlider("rx", "rx", -Math.PI, Math.PI, 0.01);
  addSlider("ry", "ry", -Math.PI, Math.PI, 0.01);
  addSlider("rz", "rz", -Math.PI, Math.PI, 0.01);
  addSlider("scale", "s", 0.1, 20, 0.01);

  const copyBtn = document.createElement("button");
  copyBtn.className = "splat-copy";
  copyBtn.textContent = "copy CLI flags";
  copyBtn.title = "Copy --splat-* flags for export_viewer.py";
  copyBtn.addEventListener("click", () => {
    const cmd =
      `  --splat-position ${state.px.toFixed(3)} ${state.py.toFixed(3)} ${state.pz.toFixed(3)} \\\n` +
      `  --splat-rotation-euler ${state.rx.toFixed(3)} ${state.ry.toFixed(3)} ${state.rz.toFixed(3)} \\\n` +
      `  --splat-scale ${state.s.toFixed(3)}`;
    navigator.clipboard.writeText(cmd).then(
      () => { copyBtn.textContent = "copied!"; setTimeout(() => copyBtn.textContent = "copy CLI flags", 1500); },
      () => { copyBtn.textContent = "copy failed"; },
    );
  });
  body.appendChild(copyBtn);
}
